import React from 'react';
import { motion } from 'framer-motion';
import { Globe, TrendingUp, Users, MessageSquare } from 'lucide-react';

// Floating mockup panels — CSS/SVG based, no external assets
const AnimatedHeroVisual = () => {
  return (
    <div className="relative w-full h-[420px] lg:h-[560px]" aria-hidden="true">
      {/* Ambient glow */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[380px] h-[380px] rounded-full bg-[#FF6B35]/10 blur-3xl" />
      </div>

      {/* Main browser panel */}
      <motion.div
        initial={{ opacity: 0, y: 30, rotate: -2 }}
        animate={{ opacity: 1, y: 0, rotate: -2 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[86%] max-w-[520px]"
      >
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0B1221] shadow-2xl shadow-black/50">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-[#050914]">
            <div className="flex gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
            </div>
            <div className="mx-auto px-3 py-1 rounded-md bg-white/5 text-[10px] tracking-widest uppercase text-white/40 font-bold">
              digitizeintl.com
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="h-2 w-24 bg-white/20 rounded" />
            <div className="h-6 w-full bg-white/10 rounded" />
            <div className="h-6 w-3/4 bg-white/10 rounded" />
            <div className="grid grid-cols-3 gap-3 pt-3">
              <div className="h-16 rounded-lg bg-gradient-to-br from-white/8 to-white/2 border border-white/5" />
              <div className="h-16 rounded-lg bg-gradient-to-br from-[#FF6B35]/20 to-transparent border border-[#FF6B35]/20" />
              <div className="h-16 rounded-lg bg-gradient-to-br from-white/8 to-white/2 border border-white/5" />
            </div>
            <div className="flex items-center justify-between pt-3">
              <div className="h-2 w-16 bg-white/15 rounded" />
              <div className="h-7 w-24 bg-white rounded-full" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating stat card top-right */}
      <motion.div
        initial={{ opacity: 0, x: 20, y: -10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="absolute top-6 right-0 lg:right-4 w-40 rounded-xl border border-white/10 bg-[#0B1221]/90 backdrop-blur p-4 shadow-xl"
      >
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4 text-[#FF6B35]" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Growth</p>
        </div>
        <p className="font-display text-2xl text-white font-medium tracking-tight">+38%</p>
        <p className="text-[11px] text-white/40 mt-1">this quarter</p>
      </motion.div>

      {/* Floating CRM card bottom-left */}
      <motion.div
        initial={{ opacity: 0, x: -20, y: 10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.8, delay: 0.55 }}
        className="absolute bottom-8 left-0 lg:left-2 w-48 rounded-xl border border-white/10 bg-[#0B1221]/90 backdrop-blur p-4 shadow-xl"
      >
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-[#FF6B35]" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">New Lead</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center">
            <Users className="w-3 h-3 text-white/70" />
          </div>
          <div>
            <div className="h-2 w-20 bg-white/20 rounded mb-1" />
            <div className="h-1.5 w-14 bg-white/10 rounded" />
          </div>
        </div>
      </motion.div>

      {/* Floating small ping */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="absolute top-1/3 -right-2 lg:right-6 w-11 h-11 rounded-full border border-white/10 bg-[#0B1221]/90 backdrop-blur flex items-center justify-center shadow-xl"
      >
        <Globe className="w-4 h-4 text-white/60" />
      </motion.div>
    </div>
  );
};

export default AnimatedHeroVisual;
